import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SocketContext } from '../SocketProvider';
import { getFromServer } from '../components/Common/requests';

const ACTIVE_STATUSES = new Set(['queued', 'running']);
const LATEST_JOB_KEY = 'jobberLatestSyncJobId';
const POLL_INTERVAL_MS = 3000;

export function useJobberSync(type) {
  const { socket } = useContext(SocketContext);
  const user = useSelector((store) => store.user?.user?.data);
  const [jobs, setJobs] = useState([]);
  const jobsRef = useRef([]);

  const mergeJobs = useCallback((incomingJobs) => {
    const incoming = (Array.isArray(incomingJobs) ? incomingJobs : [incomingJobs])
      .filter((job) => job?.id);
    if (!incoming.length) return;

    window.localStorage.setItem(LATEST_JOB_KEY, incoming[0].id);
    setJobs((current) => {
      const incomingIds = new Set(incoming.map((job) => job.id));
      const next = [...incoming, ...current.filter((job) => !incomingIds.has(job.id))];
      jobsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user?._id) {
      jobsRef.current = [];
      setJobs([]);
      return undefined;
    }

    jobsRef.current = [];
    setJobs([]);
    let mounted = true;
    let polling = false;
    const latestJobId = window.localStorage.getItem(LATEST_JOB_KEY);

    const restoreJobs = async () => {
      const requests = [getFromServer('jobber-syncs/active')];
      if (latestJobId) requests.push(getFromServer(`jobber-syncs/${latestJobId}`));
      const results = await Promise.allSettled(requests);
      if (!mounted) return;
      const restored = results
        .filter((result) => result.status === 'fulfilled' && result.value?.status)
        .flatMap((result) => Array.isArray(result.value.data) ? result.value.data : [result.value.data])
        .filter(Boolean);
      mergeJobs(Array.from(new Map(restored.map((job) => [job.id, job])).values()));
    };

    const pollJobs = async () => {
      if (polling) return;
      polling = true;
      try {
        const activeResult = await getFromServer('jobber-syncs/active');
        if (!mounted || !activeResult?.status) return;

        const activeJobs = Array.isArray(activeResult.data) ? activeResult.data : [];
        const activeIds = new Set(activeJobs.map((job) => job.id));
        const missedTerminalIds = jobsRef.current
          .filter((job) => ACTIVE_STATUSES.has(job.status) && !activeIds.has(job.id))
          .map((job) => job.id);
        const terminalResults = await Promise.allSettled(
          missedTerminalIds.map((jobId) => getFromServer(`jobber-syncs/${jobId}`))
        );
        if (!mounted) return;

        const terminalJobs = terminalResults
          .filter((result) => result.status === 'fulfilled' && result.value?.status)
          .map((result) => result.value.data)
          .filter(Boolean);
        mergeJobs([...activeJobs, ...terminalJobs]);
      } finally {
        polling = false;
      }
    };

    restoreJobs();
    const pollTimer = window.setInterval(pollJobs, POLL_INTERVAL_MS);
    return () => {
      mounted = false;
      window.clearInterval(pollTimer);
    };
  }, [mergeJobs, user?._id]);

  useEffect(() => {
    if (!socket || !user?._id) return undefined;
    const eventName = `JobberSyncUpdated_${user._id}`;
    const handler = (job) => mergeJobs(job);
    socket.on(eventName, handler);
    return () => socket.off(eventName, handler);
  }, [mergeJobs, socket, user?._id]);

  const trackJob = useCallback((syncJob) => {
    mergeJobs(syncJob);
  }, [mergeJobs]);

  const job = useMemo(
    () => jobs.find((item) => !type || item.type === type) || null,
    [jobs, type]
  );

  return {
    job,
    jobs,
    isActive: Boolean(job && ACTIVE_STATUSES.has(job.status)),
    progress: Number(job?.progress) || 0,
    retryAt: job?.retryAt || null,
    authRequired: job?.error?.code === 'JOBBER_AUTH_REQUIRED',
    trackJob,
  };
}
